import React, { FunctionComponent, useContext, useState } from 'react';
import { Insulation, Requirement, Subsoil } from '../../../Api/AccessoriesApi';
import { Icon } from '../../../Component/Icon/Icon';
import { Radio } from '../../../Component/Radio/Radio';
import { Checkbox } from '../../../Component/Checkbox/Checkbox';
import { ShoppingPlannerContext } from '../../../Context/ShoppingPlannerContext';
import { useProductsFromStates } from '../../../Hook/UseProductsFromStates';
import { useResponsive } from '../../../Hook/UseResponsive';
import { Link } from 'react-router-dom';
import { AccessoriesPage } from '../AccessoriesPage';
import { urls } from '../../../Constants/Urls';

import './InsulationPage.scss';
import '../../../Component/SelectionMarker/SelectionMarker.scss';

export interface InsulationFilter {
    activeSubsoil: Subsoil | null;
    activeRequirements: Requirement[];
}

export const InsulationPage: FunctionComponent = () => {
    const { accessories, selectedInsulation, setSelectedInsulation, insulationFilter } =
        useContext(ShoppingPlannerContext);
    const { isLargerThanMd } = useResponsive();

    const { floor } = useProductsFromStates();

    const filteredInsulations = filterInsulations(accessories.insulations, insulationFilter).filter(
        (insulation) => floor && insulation.suitableFloorFamilies.includes(floor.floorFamilyId)
    );

    const checked = (requirement: Requirement) =>
        insulationFilter.activeRequirements.some((activeRequirements) => activeRequirements === requirement);

    return (
        <AccessoriesPage className="shopping-planner-insulation-page">
            {isLargerThanMd ? <InsulationFilter checked={checked} /> : null}

            <div className="shopping-planner__scroll-area">
                <div className="shopping-planner-accessories-page__accessories">
                    <h2 className="shopping-planner-accessories-page__headline">Dämmung</h2>
                    {isLargerThanMd ? null : (
                        <>
                            <div className="shopping-planner-accessories-page__breadcrumbs">
                                <h2 className="shopping-planner-accessories-page__headline">Dämmung</h2>
                                <Icon icon="arrow" />
                                <Link
                                    to={urls.steps.accessoriesSkirting.builder()}
                                    className="shopping-planner-accessories-page__headline"
                                >
                                    Leisten
                                </Link>
                                <Icon icon="arrow" />
                                <Link
                                    to={urls.steps.accessoriesInstallation.builder()}
                                    className="shopping-planner-accessories-page__headline"
                                >
                                    Verlegung & Pflege
                                </Link>
                            </div>
                            <InsulationFilter checked={checked} />
                        </>
                    )}
                    {floor?.doesNotNeedInsulation ? (
                        <p>Für den gewählten Boden wird keine Dämmung benötigt.</p>
                    ) : (
                        <InsulationList
                            filteredInsulations={filteredInsulations}
                            selectedInsulation={selectedInsulation}
                            setSelectedInsulation={setSelectedInsulation}
                        />
                    )}
                </div>
            </div>
        </AccessoriesPage>
    );
};

interface InsulationListProps {
    filteredInsulations: Insulation[];
    selectedInsulation: string;
    setSelectedInsulation: (articleNumber: string) => void;
}

const InsulationList: FunctionComponent<InsulationListProps> = (props) => {
    if (props.filteredInsulations.length === 0) {
        return <p>Keine Dämmungen gefunden welche dem gewählten Boden und den Anforderungen entsprechen.</p>;
    }
    return (
        <ul className="shopping-planner-insulations">
            {props.filteredInsulations.map((insulation) => (
                <li
                    key={insulation.articleNumber}
                    className={[
                        'shopping-planner-insulation',
                        insulation.articleNumber === props.selectedInsulation
                            ? 'shopping-planner-insulation--selected'
                            : '',
                    ].join(' ')}
                    onClick={() => {
                        props.setSelectedInsulation(
                            insulation.articleNumber === props.selectedInsulation ? '' : insulation.articleNumber
                        );
                    }}
                >
                    <div style={{ position: 'relative' }}>
                        <img src={insulation.imageUri ?? ''}></img>
                    </div>
                    <div className="shopping-planner-insulation__description">
                        <div className="shopping-planner-insulation__title">{insulation.label}</div>
                        <ul>
                            {insulation.properties.map((property) => (
                                <li className="shopping-planner-insulation__property"> {property.label}</li>
                            ))}
                        </ul>
                    </div>
                    {insulation.articleNumber === props.selectedInsulation ? (
                        <Icon icon="check" className="shopping-planner-selection-marker" />
                    ) : null}
                </li>
            ))}
        </ul>
    );
};

interface InsulationFilterProps {
    checked: (requirement: Requirement) => boolean;
}

const InsulationFilter: FunctionComponent<InsulationFilterProps> = (props) => {
    const { insulationFilter, setInsulationFilter } = useContext(ShoppingPlannerContext);
    const { isLargerThanMd } = useResponsive();

    const [filterHidden, setFilterHidden] = useState(!isLargerThanMd);

    const addOrRemoveActiveRequirement = (requirement: Requirement) => {
        if (props.checked(requirement)) {
            setInsulationFilter((currentInsulationFilter) => ({
                ...currentInsulationFilter,
                activeRequirements: currentInsulationFilter.activeRequirements.filter(
                    (activeRequirement) => activeRequirement !== requirement
                ),
            }));
        } else {
            setInsulationFilter((currentInsulationFilter) => ({
                ...currentInsulationFilter,
                activeRequirements: [...currentInsulationFilter.activeRequirements, requirement],
            }));
        }
    };

    const updateSubsoilInFilter = (subsoil: Subsoil) => {
        setInsulationFilter((currentInsulationFilter) => ({ ...currentInsulationFilter, activeSubsoil: subsoil }));
    };

    return (
        <aside className="shopping-planner-sidebar">
            {isLargerThanMd ? null : (
                <button
                    type="button"
                    className="shopping-planner-accessories-page__filter-button"
                    onClick={() => setFilterHidden((currentFilterHidden) => !currentFilterHidden)}
                >
                    Filter
                    <Icon icon="filter" />
                </button>
            )}
            {filterHidden ? null : (
                <>
                    <div className="shopping-planner-insulation-page__soil-filter">
                        <h3 className="shopping-planner-sidebar__headline">
                            Auf welchem Untergrund soll der Boden verlegt werden
                        </h3>
                        <Radio
                            type="radio"
                            onChange={() => updateSubsoilInFilter('screed')}
                            checked={insulationFilter.activeSubsoil === 'screed'}
                        >
                            Estrich
                        </Radio>
                        <Radio
                            type="radio"
                            onChange={() => updateSubsoilInFilter('wood')}
                            checked={insulationFilter.activeSubsoil === 'wood'}
                        >
                            Holz
                        </Radio>
                        <Radio
                            type="radio"
                            onChange={() => updateSubsoilInFilter('tiles')}
                            checked={insulationFilter.activeSubsoil === 'tiles'}
                        >
                            Fliesen
                        </Radio>
                        <Radio
                            type="radio"
                            onChange={() => updateSubsoilInFilter('other')}
                            checked={insulationFilter.activeSubsoil === 'other'}
                        >
                            Andere
                        </Radio>
                    </div>
                    <div className="shopping-planner-insulation-page__requirements-filter">
                        <h3 className="shopping-planner-sidebar__headline">
                            Welche Anforderungen soll die Dämmung erfüllen?
                        </h3>
                        <Checkbox
                            type="checkbox"
                            onChange={() => addOrRemoveActiveRequirement('unevenSubsoil')}
                            checked={props.checked('unevenSubsoil')}
                        >
                            unebene Untergründe
                        </Checkbox>
                        <Checkbox
                            type="checkbox"
                            onChange={() => addOrRemoveActiveRequirement('soundproofing')}
                            checked={props.checked('soundproofing')}
                        >
                            Tritt- und Gehschalldämmung
                        </Checkbox>
                        <Checkbox
                            type="checkbox"
                            onChange={() => addOrRemoveActiveRequirement('thermalInsulation')}
                            checked={props.checked('thermalInsulation')}
                        >
                            Wärmedämmung
                        </Checkbox>
                        <Checkbox
                            type="checkbox"
                            onChange={() => addOrRemoveActiveRequirement('underfloorHeating')}
                            checked={props.checked('underfloorHeating')}
                        >
                            Fußbodenheizung
                        </Checkbox>
                        <Checkbox
                            type="checkbox"
                            onChange={() => addOrRemoveActiveRequirement('vaporBarrier')}
                            checked={props.checked('vaporBarrier')}
                        >
                            Dampfbremse
                        </Checkbox>
                        <Checkbox
                            type="checkbox"
                            onChange={() => addOrRemoveActiveRequirement('stress')}
                            checked={props.checked('stress')}
                        >
                            hohe Belastungen
                        </Checkbox>
                    </div>
                </>
            )}
        </aside>
    );
};

const filterInsulations = (insulations: Insulation[], insulationFilter: InsulationFilter) => {
    if (insulationFilter.activeRequirements.length === 0 && insulationFilter.activeSubsoil === null) {
        return insulations;
    }

    // filter active subsoil
    let newFilteredInsulations = insulations.filter((insulation) =>
        insulationFilter.activeSubsoil ? insulation.filter.subsoil.includes(insulationFilter.activeSubsoil) : true
    );

    // filter active requirement. A decor must fulfill all requirements (hence the double filtering).
    if (insulationFilter.activeRequirements.length > 0) {
        newFilteredInsulations = newFilteredInsulations.filter((insulation) =>
            insulationFilter.activeRequirements.every((requirement) =>
                insulation.filter.requirements.includes(requirement)
            )
        );
    }

    return newFilteredInsulations;
};
